'use client'

import * as React from 'react'
import Link from 'next/link'
import { Check, CircleAlert, Loader2, MailCheck } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { START_ERROR_CODES } from '@/lib/start/start-error-codes'

import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/encuentro/ui/field'
import { cn } from '@/lib/utils'
import { EncuadrePiece } from '@/components/encuentro/marketing/encuadre-frame'
import { Eyebrow, TwoPartTitle } from '@/components/encuentro/marketing/marketing-parts'
import { authLinkClass } from '@/components/encuentro/screens/auth/auth-shell'
import { SignupShell } from '@/components/encuentro/screens/school-signup/signup-shell'
import { SlugField, type SlugTone } from '@/components/encuentro/screens/school-signup/slug-field'
import {
  useStartForm,
  type StartFormActions,
  type StartFormState,
  type StartSlugStatus,
} from '@/hooks/use-start-form'

const SLUG_TONES: Record<StartSlugStatus, SlugTone> = {
  idle: 'muted',
  checking: 'checking',
  available: 'positive',
  taken: 'danger',
  reserved: 'danger',
  invalid: 'muted',
}

export interface StartScreenProps {
  initialEmail?: string
  initialName?: string
  initialSlug?: string
  /** `requestOrgCreation` and `checkSlugAvailability` from app/start/actions.ts. */
  actions: StartFormActions
  /** The language switcher. */
  locale?: React.ReactNode
}

/**
 * The new-school sign-up in Encuentro (ALU-118): the public site's promise on
 * one side and the form on the other, inside the Encuadre (the blue piece cut
 * by the top left, the spark by the bottom right), as the site's hero frames
 * the product. On the phone the promise's title comes first, then the form,
 * then the three reasons.
 *
 * Everything the form does is the classic one's, through `useStartForm`: the
 * draft kept in the browser, the address that follows the name, the live
 * availability check, the same five fields to `requestOrgCreation`, the link
 * by email and its 30 s cooldown. The copy follows the argumentario: the
 * free start, one minute to join, the school's brand, and only the relative
 * adoption figures.
 */
export function StartScreen({
  initialEmail,
  initialName,
  initialSlug,
  actions,
  locale,
}: StartScreenProps) {
  const t = useTranslations('encuentro.schoolSignup.start')
  const form = useStartForm({
    initialEmail,
    initialName,
    initialSlug,
    actions,
    text: {
      networkReady: t('toast.networkReady'),
      newLinkSent: t('toast.newLinkSent'),
      errors: Object.fromEntries(START_ERROR_CODES.map((code) => [code, t(`errors.${code}`)])),
    },
  })

  return (
    <SignupShell locale={locale}>
      <div
        data-testid="encuentro-start"
        data-state={form.submitted ? 'sent' : 'form'}
        className="mx-auto grid w-full max-w-6xl grid-cols-[minmax(0,1fr)] gap-6 px-4 py-6 md:gap-8 md:px-6 md:py-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,32rem)] lg:gap-x-16 lg:gap-y-8 lg:py-16"
      >
        {/* ALU-416: on a phone the form is the first thing after one title and
            one subtitle (it used to start at 63 % of the screen, behind two of
            each). The eyebrow and the card's own heading come back beside it
            on a computer, where the promise and the form sit side by side. */}
        <div className="grid content-start gap-3 lg:col-start-1 lg:row-start-1 lg:gap-4">
          <Eyebrow className="hidden lg:block">{t('eyebrow')}</Eyebrow>
          <TwoPartTitle as="h1" lead={t('title')} accent={t('accent')} className="lg:text-5xl" />
          <p className="text-lg leading-relaxed text-en-ink-muted">{t('subtitle')}</p>
        </div>

        <div className="relative lg:col-start-2 lg:row-span-2 lg:row-start-1">
          <div
            aria-hidden
            className="pointer-events-none absolute -inset-6 hidden overflow-hidden rounded-en-lg bg-en-canvas lg:block"
          >
            <EncuadrePiece piece="left" className="absolute -top-16 -left-6 w-40" />
            <EncuadrePiece piece="right" className="absolute -right-4 -bottom-16 w-36" />
          </div>
          <div className="relative grid grid-cols-[minmax(0,1fr)] gap-6 rounded-en-lg bg-en-surface p-5 shadow-en-card ring-1 ring-en-line md:p-8">
            {form.submitted ? <SentPanel form={form} /> : <StartFields form={form} />}
          </div>
        </div>

        <div className="grid content-start gap-6 lg:col-start-1 lg:row-start-2">
          <ul className="grid gap-4">
            {(['free', 'minute', 'brand'] as const).map((key) => (
              <li key={key} className="flex items-start gap-3 text-en-body">
                <span
                  aria-hidden
                  className="mt-px grid size-6 shrink-0 place-items-center rounded-en-full bg-en-electric-soft text-en-electric"
                >
                  <Check className="size-4" />
                </span>
                <span>{t(`points.${key}`)}</span>
              </li>
            ))}
          </ul>
          <p className="rounded-en-md bg-en-surface-sunken p-4 text-en-meta text-en-ink-muted">
            {t('proof')}
          </p>
        </div>
      </div>
    </SignupShell>
  )
}

function StartFields({ form }: { form: StartFormState }) {
  const t = useTranslations('encuentro.schoolSignup.start.form')
  const attestId = React.useId()
  const {
    name,
    slug,
    email,
    setEmail,
    fullName,
    setFullName,
    attested,
    setAttested,
    errors,
    isPending,
    slugStatus,
    canSubmit,
    handleNameChange,
    handleSlugChange,
    handleSubmit,
  } = form

  const headingId = React.useId()

  return (
    <>
      <CardHeading
        id={headingId}
        title={t('title')}
        subtitle={t('subtitle')}
        className="hidden lg:grid"
      />
      <form
        action={handleSubmit}
        aria-labelledby={headingId}
        className="grid grid-cols-[minmax(0,1fr)] gap-5"
      >
        <Field
          label={t('name')}
          id="name"
          name="name"
          placeholder={t('namePlaceholder')}
          value={name}
          onChange={(e) => handleNameChange(e.target.value)}
          maxLength={80}
          autoComplete="organization"
          required
          error={errors.name}
        />
        <SlugField
          label={t('slug')}
          prefix={t('slugPrefix')}
          id="slug"
          name="slug"
          placeholder={t('slugPlaceholder')}
          value={slug}
          onChange={(e) => handleSlugChange(e.target.value)}
          maxLength={40}
          required
          error={errors.slug}
          status={{ tone: SLUG_TONES[slugStatus], text: t(`slugStatus.${slugStatus}`) }}
        />
        <div className="grid grid-cols-[minmax(0,1fr)] gap-5 sm:grid-cols-2">
          <Field
            label={t('fullName')}
            id="fullName"
            name="fullName"
            placeholder={t('fullNamePlaceholder')}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            maxLength={80}
            autoComplete="name"
            required
            error={errors.fullName}
          />
          <Field
            label={t('email')}
            id="email"
            name="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder={t('emailPlaceholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            error={errors.email}
          />
        </div>

        <div className="grid gap-2">
          <div className="flex items-start gap-3 rounded-en-md bg-en-surface-sunken p-4">
            <span className="relative grid size-6 shrink-0 place-items-center">
              <input
                id={attestId}
                name="attested"
                type="checkbox"
                checked={attested}
                aria-invalid={errors.attested ? true : undefined}
                aria-describedby={errors.attested ? `${attestId}-error` : undefined}
                onChange={(e) => setAttested(e.target.checked)}
                className="peer size-6 cursor-pointer appearance-none rounded-en-sm border border-en-line-control bg-en-surface checked:border-en-ink checked:bg-en-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid focus-visible:outline-en-focus"
              />
              <Check
                aria-hidden
                className="pointer-events-none absolute size-4 text-en-bg opacity-0 peer-checked:opacity-100"
              />
            </span>
            <label htmlFor={attestId} className="cursor-pointer text-en-meta text-en-ink">
              {t.rich('attest', {
                terms: (chunks) => (
                  <Link href="/policies/terms" target="_blank" className={authLinkClass}>
                    {chunks}
                  </Link>
                ),
                privacy: (chunks) => (
                  <Link href="/policies/privacy" target="_blank" className={authLinkClass}>
                    {chunks}
                  </Link>
                ),
              })}
            </label>
          </div>
          {errors.attested && (
            <p
              id={`${attestId}-error`}
              className="flex items-start gap-2 text-en-meta text-en-danger"
            >
              <CircleAlert aria-hidden className="mt-px size-4 shrink-0" />
              <span>{errors.attested}</span>
            </p>
          )}
        </div>

        {errors.general && <Alert look="encuentro" tone="danger" body={errors.general} />}

        <div className="grid gap-3">
          <Button look="encuentro" variant="primary" type="submit" block disabled={!canSubmit}>
            {isPending && (
              <Loader2 aria-hidden className="animate-spin motion-reduce:animate-none" />
            )}
            {isPending ? t('submitting') : t('submit')}
          </Button>
          <p className="text-center text-en-meta text-en-ink-muted">{t('note')}</p>
        </div>
      </form>
    </>
  )
}

function SentPanel({ form }: { form: StartFormState }) {
  const t = useTranslations('encuentro.schoolSignup.start.sent')
  const { submitted, resendIn, isResending, handleResend, editAgain } = form
  if (!submitted) return null
  return (
    <div data-testid="encuentro-start-sent" className="grid justify-items-start gap-6">
      <span
        aria-hidden
        className="grid size-12 place-items-center rounded-en-full bg-en-positive-soft text-en-positive"
      >
        <MailCheck className="size-6" />
      </span>
      <CardHeading
        title={t('title')}
        subtitle={t.rich('body', {
          email: submitted.email,
          strong: (chunks) => (
            <strong className="text-en-ink [overflow-wrap:anywhere]">{chunks}</strong>
          ),
        })}
      />
      <div className="grid w-full gap-3">
        <Button
          look="encuentro"
          variant="secondary"
          block
          onClick={handleResend}
          disabled={resendIn > 0 || isResending}
        >
          {isResending && (
            <Loader2 aria-hidden className="animate-spin motion-reduce:animate-none" />
          )}
          {isResending
            ? t('sending')
            : resendIn > 0
              ? t('resendIn', { seconds: resendIn })
              : t('resend')}
        </Button>
        <p className="text-center text-en-meta text-en-ink-muted">
          {t('wrongEmail')}{' '}
          <button type="button" onClick={editAgain} className={`font-semibold ${authLinkClass}`}>
            {t('edit')}
          </button>
        </p>
      </div>
    </div>
  )
}

/**
 * The card's own title: an `h2`, since the page's `h1` is the promise. The
 * form's is only on a computer (`className`); on a phone it still names the
 * form (`aria-labelledby` reads hidden text).
 */
function CardHeading({
  id,
  title,
  subtitle,
  className,
}: {
  id?: string
  title: string
  subtitle: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('grid gap-2', className)}>
      <h2 id={id} className="text-en-title text-balance [overflow-wrap:anywhere]">
        {title}
      </h2>
      <p className="text-en-body text-en-ink-muted">{subtitle}</p>
    </div>
  )
}
